import { getSessionToken } from './sessionTokenService';

interface UserTickerList {
  id: number;
  list_name: string;
  app_user_id: number;
  status: string;
  created_at: string;
  updated_at: string;
  ticker_count?: number;
  public_uuid?: string | null;
  share_enabled?: boolean;
  public_list_name?: string | null;
}

interface TickerInList {
  id: number;
  ticker_symbol: string;
  order_num: number;
  added_at: string;
}

interface UserTickerListWithTickers {
  id: number;
  list_name: string;
  app_user_id: number;
  status: string;
  created_at: string;
  updated_at: string;
  tickers: TickerInList[];
}

interface UserListPreferences {
  id: number;
  createdAt: string;
  updatedAt: string;
  userTickerListId: number;
  dailyIndicatorsToHide?: string[];
  performanceIndicatorsToHide?: string[];
  technicalIndicatorsToHide?: string[];
  fundamentalIndicatorsToHide?: string[];
  analystIndicatorsToHide?: string[];
  insiderIndicatorsToHide?: string[];
  prosConsIndicatorsToHide?: string[];
}

interface CreateUserListPreferencesRequest {
  userTickerListId: number;
  dailyIndicatorsToHide?: string[];
  performanceIndicatorsToHide?: string[];
  technicalIndicatorsToHide?: string[];
  fundamentalIndicatorsToHide?: string[];
  analystIndicatorsToHide?: string[];
  insiderIndicatorsToHide?: string[];
  prosConsIndicatorsToHide?: string[];
}

interface UpdateUserListPreferencesRequest {
  dailyIndicatorsToHide?: string[];
  performanceIndicatorsToHide?: string[];
  technicalIndicatorsToHide?: string[];
  fundamentalIndicatorsToHide?: string[];
  analystIndicatorsToHide?: string[];
  insiderIndicatorsToHide?: string[];
  prosConsIndicatorsToHide?: string[];
}

interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
}

class UserTickerListApiService {
  private baseUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/v1/user-ticker-list`;

  private async makeRequest<T>(url: string, options: RequestInit = {}): Promise<T> {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      throw new Error('No access token found');
    }

    // Add session token to all requests
    const sessionToken = getSessionToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...(options.headers as Record<string, string>),
    };
    if (sessionToken) {
      headers['X-Session-Token'] = sessionToken;
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
      throw new Error(errorData.message || `HTTP ${response.status}`);
    }

    return response.json();
  }

  async getUserLists(): Promise<UserTickerList[]> {
    const response = await this.makeRequest<ApiResponse<UserTickerList[]>>(`${this.baseUrl}/lists`);
    return response.data;
  }

  async getListWithTickers(listId: number): Promise<UserTickerListWithTickers> {
    const response = await this.makeRequest<ApiResponse<UserTickerListWithTickers>>(`${this.baseUrl}/${listId}`);
    return response.data;
  }

  async createList(listName: string): Promise<UserTickerList> {
    const response = await this.makeRequest<ApiResponse<UserTickerList>>(`${this.baseUrl}/create_list`, {
      method: 'POST',
      body: JSON.stringify({ list_name: listName }),
    });
    return response.data;
  }

  async addTickerToList(listId: number, tickerSymbol: string, orderNum?: number): Promise<void> {
    await this.makeRequest(`${this.baseUrl}/${listId}/tickers`, {
      method: 'POST',
      body: JSON.stringify({ 
        ticker_symbol: tickerSymbol,
        ...(orderNum && { order_num: orderNum })
      }),
    });
  }

  async removeTickerFromList(listId: number, tickerId: number): Promise<void> {
    await this.makeRequest(`${this.baseUrl}/${listId}/tickers/${tickerId}`, {
      method: 'DELETE',
    });
  }

  async updateListName(listId: number, newName: string): Promise<UserTickerList> {
    const response = await this.makeRequest<ApiResponse<UserTickerList>>(`${this.baseUrl}/${listId}`, {
      method: 'PUT',
      body: JSON.stringify({ list_name: newName }),
    });
    return response.data;
  }

  async updateList(
    listId: number,
    updates: {
      list_name: string;
      public_list_name?: string;
      share_enabled?: boolean;
    }
  ): Promise<UserTickerList> {
    const response = await this.makeRequest<ApiResponse<UserTickerList>>(`${this.baseUrl}/${listId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
    return response.data;
  }

  async updateTickerMappings(listId: number, tickerMappings: Array<{ ticker_symbol: string; order_num: number }>): Promise<ApiResponse<any>> {
    const response = await this.makeRequest<ApiResponse<any>>(`${this.baseUrl}/${listId}/mappings`, {
      method: 'PUT',
      body: JSON.stringify({ ticker_mappings: tickerMappings }),
    });
    return response;
  }

  // UserListPreferences methods
  async createUserListPreferences(request: CreateUserListPreferencesRequest): Promise<ApiResponse<UserListPreferences>> {
    const response = await this.makeRequest<ApiResponse<UserListPreferences>>(`${this.baseUrl}/preferences`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
    return response;
  }

  async updateUserListPreferences(userTickerListId: number, request: UpdateUserListPreferencesRequest): Promise<ApiResponse<UserListPreferences>> {
    const response = await this.makeRequest<ApiResponse<UserListPreferences>>(`${this.baseUrl}/preferences/${userTickerListId}`, {
      method: 'PUT',
      body: JSON.stringify(request),
    });
    return response;
  }

  async getUserListPreferences(userTickerListId: number): Promise<ApiResponse<UserListPreferences | null>> {
    const response = await this.makeRequest<ApiResponse<UserListPreferences | null>>(`${this.baseUrl}/preferences/${userTickerListId}`, {
      method: 'GET',
    });
    return response;
  }

  async deleteUserListPreferences(userTickerListId: number): Promise<ApiResponse<void>> {
    const response = await this.makeRequest<ApiResponse<void>>(`${this.baseUrl}/preferences/${userTickerListId}`, {
      method: 'DELETE',
    });
    return response;
  }

  async deleteList(listId: number): Promise<ApiResponse<void>> {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      throw new Error('No access token found');
    }

    // Add session token to all requests
    const sessionToken = getSessionToken();
    const headers: Record<string, string> = {
      'Authorization': `Bearer ${token}`,
    };
    if (sessionToken) {
      headers['X-Session-Token'] = sessionToken;
    }

    const response = await fetch(`${this.baseUrl}/${listId}`, {
      method: 'DELETE',
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
      throw new Error(errorData.message || `HTTP ${response.status}`);
    }

    return response.json();
  }
}

export const userTickerListApi = new UserTickerListApiService();
export type { 
  UserTickerList, 
  TickerInList, 
  UserTickerListWithTickers, 
  UserListPreferences, 
  CreateUserListPreferencesRequest, 
  UpdateUserListPreferencesRequest 
};
